import React from 'react'
import MODULE_COMPONENTS from './modules'
import CompactServiceDetail from './modules/CompactServiceDetail'
import PageFrame from './modules/PageFrame'
import { interpolate } from './utils/proposalVariables'
import { isAsNeeded, isIncludedWithPlan } from './utils/frequencyConfig'
import { hasEstimateContract } from './utils/estimateContract'
import { paginateIntroLetter, paginateNotesPage } from './utils/introRichText'
import { paginateTermsPage } from './utils/termsPagination'

// Modules that fill exactly one page
const FIXED_PAGE_MODULES = new Set(['cover'])

const ProposalPreview = ({ proposal, getModuleData, theme, variables }) => {
  if (!proposal) return null

  const sortedModules = [...(proposal.modules || [])].sort((a, b) => a.order - b.order)
  const crmSnapshot = proposal.crmSnapshot || {}
  const usingContract = hasEstimateContract(proposal.estimate)

  // Build pages: fixed-page modules get their own page,
  // flow modules get grouped, and service detail pages each get their own fixed page.
  const pages = []

  sortedModules.forEach((mod) => {
    if (!mod.enabled) return
    const Component = MODULE_COMPONENTS[mod.moduleId]
    if (!Component) return

    const data = getModuleData(mod.moduleId)

    // Skip serviceArea if there's no map, saved service areas, or property data to show
    if (
      mod.moduleId === 'serviceArea' &&
      !data.mapImageUrl &&
      !(data.serviceAreas && data.serviceAreas.length) &&
      !(data.propertyLines && data.propertyLines.length)
    ) return
    const extraProps = {}
    if (mod.moduleId === 'project' || mod.moduleId === 'terms') {
      extraProps.scopeData = getModuleData('scope')
    }

    if (FIXED_PAGE_MODULES.has(mod.moduleId)) {
      // Cover gets its own fixed page (no frame)
      pages.push({
        key: mod.moduleId,
        fixed: true,
        noFrame: true,
        items: [{ mod, Component, data, extraProps }]
      })
    } else if (mod.moduleId === 'scope') {
      // Scope summary table goes in a flow page
      pages.push({
        key: 'scope-table',
        fixed: false,
        items: [{ mod, Component, data, extraProps, renderMode: 'table-only' }]
      })

      // Service details: compact mode groups 4 per page; full mode keeps the
      // legacy one-service-per-page layout. All default services render even
      // when the selected tier doesn't include them — customers should see
      // everything we CAN do (per-service hide stays available in the editor).
      const detailSource = usingContract ? getModuleData('scopeDetailSource') : data
      const visibleServices = (detailSource?.services || []).filter(svc => (
        !svc.hidden
        && !svc.hideDetailPage
        && svc.description
      ))
      if ((detailSource?.detailRenderMode || 'compact') === 'compact') {
        const CHUNK = 4
        for (let i = 0; i < visibleServices.length; i += CHUNK) {
          pages.push({
            key: `service-detail-${i}`,
            fixed: true,
            items: [{
              mod: { moduleId: `service-detail-${i}` },
              Component: CompactServiceDetail,
              data: { services: visibleServices.slice(i, i + CHUNK), continued: i > 0 },
              extraProps: {}
            }]
          })
        }
      } else {
        visibleServices.forEach((svc, idx) => {
          pages.push({
            key: `service-detail-${idx}`,
            fixed: true,
            serviceDetail: { svc, idx, variables }
          })
        })
      }
    } else if (mod.moduleId === 'intro') {
      paginateIntroLetter(data).forEach((introPageData, idx) => {
        pages.push({
          key: `intro-${idx}`,
          fixed: true,
          items: [{ mod, Component, data: introPageData, extraProps }]
        })
      })
    } else if (mod.moduleId === 'notes') {
      paginateNotesPage(data).forEach((notesPageData, idx) => {
        pages.push({
          key: `notes-${idx}`,
          fixed: true,
          items: [{ mod, Component, data: notesPageData, extraProps }]
        })
      })
    } else if (mod.moduleId === 'terms') {
      paginateTermsPage(data, !!extraProps.scopeData?.flavors?.length).forEach((termsPageData, idx) => {
        pages.push({
          key: `terms-${idx}`,
          fixed: true,
          items: [{ mod, Component, data: termsPageData, extraProps }]
        })
      })
    } else {
      // Flow content — put in its own flow page
      pages.push({
        key: mod.moduleId,
        fixed: false,
        items: [{ mod, Component, data, extraProps }]
      })
    }
  })

  // Calculate page numbers (skip cover)
  let pageNum = 0
  const totalPages = pages.filter(p => !p.noFrame).length

  return (
    <div className="proposal-preview">
      {pages.map((page) => {
        if (!page.noFrame) pageNum++

        if (page.serviceDetail) {
          return (
            <ServiceDetailPage
              key={page.key}
              svc={page.serviceDetail.svc}
              idx={page.serviceDetail.idx}
              variables={page.serviceDetail.variables}
              theme={theme}
              pageNumber={pageNum}
              totalPages={totalPages}
            />
          )
        }

        const pageClass = page.fixed ? 'proposal-page proposal-page--fixed' : 'proposal-page proposal-page--flow'

        const content = page.items.map(({ mod, Component, data, extraProps, renderMode }) => (
          <Component
            key={mod.moduleId}
            data={data}
            theme={theme}
            crmSnapshot={crmSnapshot}
            variables={variables}
            renderMode={renderMode}
            {...extraProps}
          />
        ))

        return (
          <div key={page.key} className={pageClass}>
            {page.noFrame ? (
              content
            ) : (
              <PageFrame theme={theme} pageNumber={pageNum} totalPages={totalPages} entityNameFormal={variables?.entityNameFormal}>
                {content}
              </PageFrame>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Standalone service detail page component
const ServiceDetailPage = ({ svc, idx, variables, theme, pageNumber, totalPages }) => {
  const DEFAULT_IMAGE_MAP = {
    'basic maintenance': '/build/images/proposals/edging.webp',
    irrigation: '/build/images/proposals/sprinklers.webp',
    trimming: '/build/images/proposals/trimming_close.webp',
    'mowing (if applicable)': '/build/images/proposals/edging.webp',
    mowing: '/build/images/proposals/edging.webp',
    'weed control & pre-emergent': '/build/images/proposals/spray_close.webp',
    'weed control': '/build/images/proposals/spray_close.webp',
    'plant replacement': '/build/images/proposals/tree_trim_close.webp',
    fertilization: '/build/images/proposals/fertilizer_sm.webp',
    'pest control': '/build/images/proposals/pest_control.webp',
    'arbor care': '/build/images/proposals/tree_trim_close.webp',
  }

  const img = svc.imageUrl || DEFAULT_IMAGE_MAP[svc.name?.toLowerCase()] || ''
  const isAlt = idx % 2 === 1
  const highlights = svc.highlights || []
  const heroHighlight = highlights[0] || null
  const gridHighlights = highlights.slice(1)
  const stats = svc.stats || []
  const checklist = svc.checklist || []
  const callout = svc.callout || null
  const highlights2 = svc.highlights2 || []

  let className = 'proposal-service-detail'
  if (!img) className += ' proposal-service-detail--no-image'
  else if (isAlt) className += ' proposal-service-detail--alt'

  return (
    <div className="proposal-page proposal-page--fixed">
      <PageFrame theme={theme} pageNumber={pageNumber} totalPages={totalPages} entityNameFormal={variables?.entityNameFormal}>
        <div className={className}>
          {img && (
            <div className="proposal-service-detail__image">
              <img src={img} alt={svc.name} />
            </div>
          )}
          <div className="proposal-service-detail__content">
            <div className="proposal-service-detail__title">{svc.name}</div>
            {isAsNeeded(svc.frequency) && isIncludedWithPlan(svc.frequency) === true && (
              <div className="proposal-service-detail__inclusion-badge proposal-service-detail__inclusion-badge--included">
                Included w/ Mtc Plan
              </div>
            )}
            {isAsNeeded(svc.frequency) && isIncludedWithPlan(svc.frequency) === false && (
              <div className="proposal-service-detail__inclusion-badge proposal-service-detail__inclusion-badge--additional">
                Additional Costs May Apply
              </div>
            )}
            <div className="proposal-service-detail__accent-line" />
            <div className="proposal-service-detail__body">{interpolate(svc.description, variables)}</div>

            {stats.length > 0 && (
              <div className="proposal-service-detail__stats">
                {stats.map((stat, i) => (
                  <div className="proposal-service-detail__stat" key={i}>
                    <div className="proposal-service-detail__stat-value">{interpolate(stat.value, variables)}</div>
                    <div className="proposal-service-detail__stat-label">{interpolate(stat.label, variables)}</div>
                  </div>
                ))}
              </div>
            )}

            {heroHighlight && (
              <div className="proposal-service-detail__hero">
                <div className="proposal-service-detail__hero-title">{interpolate(heroHighlight.title, variables)}</div>
                <div className="proposal-service-detail__hero-text">{interpolate(heroHighlight.text, variables)}</div>
              </div>
            )}

            {checklist.length > 0 && (
              <div className="proposal-service-detail__checklist">
                {checklist.map((item, i) => (
                  <div className="proposal-service-detail__check-item" key={i}>
                    <span className="proposal-service-detail__check-icon">&#10003;</span>
                    <span>{interpolate(item, variables)}</span>
                  </div>
                ))}
              </div>
            )}

            {callout && (
              <div className="proposal-service-detail__callout">
                <div className="proposal-service-detail__callout-title">{interpolate(callout.title, variables)}</div>
                <div className="proposal-service-detail__callout-text">{interpolate(callout.text, variables)}</div>
              </div>
            )}

            {gridHighlights.length > 0 && (
              <div className="proposal-service-detail__highlights">
                {gridHighlights.map((h, i) => (
                  <div className="proposal-service-detail__highlight" key={i}>
                    <div className="proposal-service-detail__highlight-title">{interpolate(h.title, variables)}</div>
                    <div className="proposal-service-detail__highlight-text">{interpolate(h.text, variables)}</div>
                  </div>
                ))}
              </div>
            )}

            {highlights2.length > 0 && (
              <div className="proposal-service-detail__highlights">
                {highlights2.map((h, i) => (
                  <div className="proposal-service-detail__highlight" key={`h2-${i}`}>
                    <div className="proposal-service-detail__highlight-title">{interpolate(h.title, variables)}</div>
                    <div className="proposal-service-detail__highlight-text">{interpolate(h.text, variables)}</div>
                  </div>
                ))}
              </div>
            )}

            {svc.footnote && (
              <div className="proposal-service-detail__footnote">{interpolate(svc.footnote, variables)}</div>
            )}
          </div>
        </div>
      </PageFrame>
    </div>
  )
}

export default ProposalPreview
